import React, { useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import moment from "moment"; // Importe o moment
import {
  Row,
  Col,
  ButtonGroup,
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
} from "reactstrap";

import api from "../../../../../services/api";
import useCancelableFetch from "../../../../../hooks/useCancelableFetch";
import ChartAreaOneLine from "../../../../../components/ChartAreaOneLine";
import { useCompanies } from "../../../../../hooks/companies"; // Importa o hook useCompanies
import { useDateFilter } from "../../../../../hooks/datefilter"; // Importe o useDateFilter

export default function ChartTotalCusto({
  deferLoad = false,
  onRegisterFetch,
}) {
  const [bigChartData, setBigChartData] = useState("totalNf");
  const [dataNf, setDataNf] = useState([]);
  const [dataPeso, setDataPeso] = useState([]);
  // Loading separado por gráfico: o de Peso ainda é lento (~30s, JOIN com NF),
  // sem motivo pra travar o spinner do gráfico de NF que retorna em sub-segundo.
  const [loadingNf, setLoadingNf] = useState(false);
  const [loadingPeso, setLoadingPeso] = useState(false);
  const { empresa, transportadora, arrayParams } = useCompanies(); // Obtém as seleções de empresa e transportadora
  const { startDate, endDate } = useDateFilter(); // Obtenha as datas do filtro

  // Datas padrão do gráfico memorizadas para não mudar a cada render
  const defaultStartDate = useMemo(
    () => moment().subtract(365, "days").startOf("month"),
    []
  );
  const defaultEndDate = useMemo(() => moment().endOf("month"), []);

  // função que carrega os dados — pode ser registrada pelo pai para execução sequencial.
  // Dispara as duas chamadas em paralelo: o spinner de cada gráfico segue o
  // estado da sua própria chamada, e o de NF (rápido) não fica refém do de Peso (lento).
  async function loadCharts() {
    const controller = createController();
    const params = {};

    // Verifique se startDate e endDate estão definidos
    if (startDate && endDate) {
      params.dataInicial = startDate.format("YYYY-MM-DD 00:00:00");
      params.dataFinal = endDate.format("YYYY-MM-DD 23:59:59");
    } else {
      // Use as datas padrão
      params.dataInicial = defaultStartDate.format("YYYY-MM-DD 00:00:00");
      params.dataFinal = defaultEndDate.format("YYYY-MM-DD 23:59:59");
    }

    if (arrayParams.embarcadores) {
      params.embarcadores = arrayParams.embarcadores;
    }

    if (arrayParams.transportadoras) {
      params.transportadoras = arrayParams.transportadoras;
    }

    setLoadingNf(true);
    setLoadingPeso(true);

    const nfPromise = api
      .get("/dashboard/custo/total-notafiscal-por-mes", {
        params,
        signal: controller.signal,
      })
      .then((response) => {
        if (response.status === 200) {
          setDataNf(response.data);
        }
      })
      .catch((error) => {
        if (error && error.code === "ERR_CANCELED") return;
        console.error("Erro ao carregar Valor de NF:", error);
      })
      .finally(() => setLoadingNf(false));

    const pesoPromise = api
      .get("/dashboard/custo/total-peso-por-mes", {
        params,
        signal: controller.signal,
      })
      .then((response) => {
        if (response.status === 200) {
          setDataPeso(response.data);
        }
      })
      .catch((error) => {
        if (error && error.code === "ERR_CANCELED") return;
        console.error("Erro ao carregar Peso Embarcado:", error);
      })
      .finally(() => setLoadingPeso(false));

    return Promise.allSettled([nfPromise, pesoPromise]);
  }

  const { createController, abort } = useCancelableFetch(30000);

  useEffect(() => {
    // registra a função de fetch no pai, caso seja fornecido
    if (onRegisterFetch) {
      onRegisterFetch(loadCharts);
    }

    // se não pediu defer, carrega imediatamente (comportamento atual)
    if (!deferLoad) {
      loadCharts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, empresa, transportadora]);

  useEffect(() => {
    return () => abort();
  }, [abort]);

  return (
    <Card style={{ boxShadow: "0px 1px 20px 0px rgb(0, 0, 0)" }}>
      <CardHeader>
        <Row>
          <Col>
            {bigChartData === "totalNf" ? (
              <>
                <h4>Valor de nota fiscal transportada</h4>
                <CardTitle>
                  Soma dos valores de notas fiscais embarcadas por mês
                </CardTitle>
              </>
            ) : (
              <>
                <h4>Total de peso embarcado</h4>
                <CardTitle>Soma de peso embarcado por mês em Kg</CardTitle>
              </>
            )}
          </Col>
          <Col sm="6">
            <ButtonGroup
              className="btn-group-toggle float-right"
              data-toggle="buttons"
            >
              <Button
                color="info"
                id="0"
                size="sm"
                tag="label"
                className={classNames("btn-simple", {
                  active: bigChartData === "totalNf",
                })}
                onClick={() => setBigChartData("totalNf")}
              >
                <input defaultChecked name="options" type="radio" />
                <span className="d-none d-sm-block d-md-block d-lg-block d-xl-block">
                  Valor de NF
                </span>
                <span className="d-block d-sm-none">
                  <i className="tim-icons icon-coins" />
                </span>
              </Button>
              <Button
                color="info"
                id="1"
                size="sm"
                tag="label"
                className={classNames("btn-simple", {
                  active: bigChartData === "totalWheight",
                })}
                onClick={() => setBigChartData("totalWheight")}
              >
                <input name="options" type="radio" />
                <span className="d-none d-sm-block d-md-block d-lg-block d-xl-block">
                  Peso Embarcado
                </span>
                <span className="d-block d-sm-none">
                  <i className="tim-icons icon-delivery-fast" />
                </span>
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        {bigChartData === "totalNf" ? (
          <ChartAreaOneLine
            data={dataNf}
            grid="rgba(29, 140, 248, 0.1)"
            lineColor="#1f8ef1"
            dataKey="valor"
            name="Valor NF Embarcado"
            measure="R$"
            yAxisWidth={100}
            loading={loadingNf}
            idLinha="totalNf"
          />
        ) : (
          <ChartAreaOneLine
            data={dataPeso}
            grid="rgba(45,206,137, 0.1)"
            lineColor="#10e211"
            dataKey="peso"
            name="Peso Embarcado"
            measure="KG"
            yAxisWidth={100}
            loading={loadingPeso}
            idLinha="totalKg"
          />
        )}
      </CardBody>
    </Card>
  );
}
